import React, { useEffect } from 'react';
import { Link } from 'react-router-dom';
import { Truck, Shield, Clock, CheckCircle, Phone, MessageCircle, Star, MapPin } from 'lucide-react';
import ComplaintsBookButton from '@/components/layout/ComplaintsBookButton';
import OrthocareFooter from '@/components/layout/OrthocareFooter';
import { useQuery } from '@tanstack/react-query';
import { base44 } from '@/api/base44Client';
import { useLanguage } from '@/lib/useLanguage';
import { getCountyDistrict } from '@/components/counties/countyDistricts';
import RealReviewsSection, { useRealReviews } from '@/components/seo/RealReviewsSection';
import SeoLangSwitcher from '@/components/seo/SeoLangSwitcher';
import OrthocareHeroSearch from '@/components/landing/OrthocareHeroSearch';
import { ui as i18nUi, localizedName, localizedFeatures, localizedCityName, buildMetaDescription, buildFaqs, buildHowTo, SUPPORTED_LANGS, categoryUi } from '@/components/seo/equipmentI18n';
import { districtRegion, regionParagraph } from '@/components/seo/regionContext';
import { equipmentUrl, districtUrl, PATH_LANGS } from '@/components/seo/langRoutes';
import { rentalEquipmentHreflang, rentalEquipmentUrl } from '@/components/seo/crossDomainHreflang';
import { siteBaseUrl, toSitePath, isGestosAgeisDomain } from '@/lib/siteDomain';
import { brandName, brandText } from '@/components/seo/brandI18n';
import { competitiveFaqs, PricingTiers, CompetitiveBanner, DeliveryZones, VisibleFaqs } from '@/components/seo/SeoCompetitiveBlocks';
import { isWheelchair, wheelchairFaqs } from '@/components/seo/wheelchairLongTailI18n';
import WheelchairLongTailSection from '@/components/seo/WheelchairLongTailSection';
import { useNoIndexMeta } from '@/hooks/useNoIndexMeta';

const equipmentData = {
  'cadeira-de-rodas': {
    name: 'Cadeira de Rodas',
    nameEn: 'Wheelchair',
    description: 'Aluguer de cadeiras de rodas manuais em Portugal com entrega ao domicílio em 24 horas, muitas vezes em 2 horas em Lisboa. Equipamento certificado CE e higienizado com protocolos hospitalares. Sem período mínimo de aluguer. Reserve 100% online: escolha as datas, assine o contrato digital e pague com MB Way, Multibanco ou cartão — sem precisar de telefonar para pedir orçamento. Entrega em Lisboa, Porto, Cascais, Sintra e todo o país.',
    descriptionEn: 'Manual wheelchair rental in Portugal with 24h home delivery, often in 2 hours in Lisbon. CE-certified equipment sanitized with hospital-grade protocols. No minimum rental period. Book 100% online: pick your dates, sign the digital contract and pay with MB Way, Multibanco or card — no need to call for a quote. Delivery in Lisbon, Porto, Cascais, Sintra and nationwide.',
    priceFrom: '5€',
    priceNum: 5,
    image: 'https://media.base44.com/images/public/691220492173ae6cbe68ab07/a2f83eb81_image.png',
    features: ['Dobrável e leve', 'Assento acolchoado', 'Travões de segurança', 'Apoio para braços e pés', 'Higienizada entre utilizações'],
    featuresEn: ['Foldable and lightweight', 'Padded seat', 'Safety brakes', 'Arm and foot rests', 'Sanitized between uses'],
    keywords: ['cadeira de rodas', 'cadeira de rodas manual', 'wheelchair', 'manual wheelchair'],
  },
  'cadeira-de-rodas-eletrica': {
    name: 'Cadeira de Rodas Elétrica',
    nameEn: 'Electric Wheelchair',
    description: 'Aluguer de cadeiras de rodas elétricas em Portugal com bateria de longa duração. Ideais para mobilidade reduzida, reabilitação e turismo. Entrega e instalação em todo o país em 24h, muitas vezes em 2 horas em Lisboa. Sem período mínimo de aluguer. Reserve 100% online: escolha as datas, assine o contrato digital e pague com MB Way, Multibanco ou cartão — sem precisar de telefonar para pedir orçamento.',
    descriptionEn: 'Electric wheelchair rental in Portugal with long-lasting battery. Ideal for reduced mobility, rehabilitation and tourism. Delivery and setup nationwide in 24h, often in 2 hours in Lisbon. No minimum rental period. Book 100% online: pick your dates, sign the digital contract and pay with MB Way, Multibanco or card — no need to call for a quote.',
    priceFrom: '25€',
    priceNum: 25,
    image: 'https://media.base44.com/images/public/691220492173ae6cbe68ab07/a2f83eb81_image.png',
    features: ['Bateria de longa duração', 'Controlo joystick', 'Assento ajustável', 'Autonomia 20km+', 'Reclinação elétrica'],
    featuresEn: ['Long-lasting battery', 'Joystick control', 'Adjustable seat', '20km+ range', 'Electric recline'],
    keywords: ['cadeira de rodas elétrica', 'cadeira elétrica', 'electric wheelchair', 'power wheelchair'],
  },
  'cama-articulada': {
    name: 'Cama Articulada',
    nameEn: 'Hospital Bed',
    description: 'Aluguer de camas articuladas elétricas hospitalares para domicílio em Portugal. Inclui colchão, grades laterais e instalação. Ideais para pós-operatório e cuidados prolongados. Entrega em 24h, muitas vezes em 2 horas em Lisboa. Sem período mínimo de aluguer. Reserve 100% online: escolha as datas, assine o contrato digital e pague com MB Way, Multibanco ou cartão — sem precisar de telefonar para pedir orçamento.',
    descriptionEn: 'Electric hospital bed rental for home use in Portugal. Includes mattress, side rails and installation. Ideal for post-surgery and extended care. 24h delivery, often in 2 hours in Lisbon. No minimum rental period. Book 100% online: pick your dates, sign the digital contract and pay with MB Way, Multibanco or card — no need to call for a quote.',
    priceFrom: '15€',
    priceNum: 15,
    image: 'https://media.base44.com/images/public/691220492173ae6cbe68ab07/a2f83eb81_image.png',
    features: ['Articulação elétrica', 'Colchão incluído', 'Grades de segurança', 'Rodas com travão', 'Instalação incluída'],
    featuresEn: ['Electric articulation', 'Mattress included', 'Safety rails', 'Brake wheels', 'Installation included'],
    keywords: ['cama articulada', 'cama elétrica', 'cama hospitalar', 'hospital bed', 'electric bed'],
  },
  'andarilho': {
    name: 'Andarilho',
    nameEn: 'Walker',
    description: 'Aluguer de andarilhos e auxiliares de marcha em Portugal. Entrega ao domicílio em 24h, muitas vezes em 2 horas em Lisboa. Equipamento ajustável em altura, com rodas e travões de segurança, certificado CE e higienizado com protocolos hospitalares. Sem período mínimo de aluguer. Reserve 100% online: escolha as datas, assine o contrato digital e pague com MB Way, Multibanco ou cartão — sem precisar de telefonar para pedir orçamento.',
    descriptionEn: 'Walker and walking aid rental in Portugal. 24h home delivery, often in 2 hours in Lisbon. Height-adjustable equipment with wheels and safety brakes, CE-certified and sanitized with hospital-grade protocols. No minimum rental period. Book 100% online: pick your dates, sign the digital contract and pay with MB Way, Multibanco or card — no need to call for a quote.',
    priceFrom: '3€',
    priceNum: 3,
    image: 'https://media.base44.com/images/public/691220492173ae6cbe68ab07/a2f83eb81_image.png',
    features: ['Ajustável em altura', 'Rodas dianteiras', 'Travões de segurança', 'Dobrável', 'Alumínio leve'],
    featuresEn: ['Height adjustable', 'Front wheels', 'Safety brakes', 'Foldable', 'Lightweight aluminum'],
    keywords: ['andarilho', 'andarilho rolante', 'walker', 'walking frame', 'rollator'],
  },
  'canadianas': {
    name: 'Canadianas',
    nameEn: 'Crutches',
    description: 'Aluguer de canadianas (muletas) em Portugal com entrega ao domicílio em 24 horas, muitas vezes em 2 horas em Lisboa. Equipamento certificado CE e higienizado com protocolos hospitalares. Sem período mínimo de aluguer. Reserve 100% online: escolha as datas, assine o contrato digital e pague com MB Way, Multibanco ou cartão — sem precisar de telefonar para pedir orçamento. Entrega em Lisboa, Porto, Cascais, Sintra e todo o país.',
    descriptionEn: 'Crutch rental in Portugal with 24h home delivery, often in 2 hours in Lisbon. CE-certified equipment sanitized with hospital-grade protocols. No minimum rental period. Book 100% online: pick your dates, sign the digital contract and pay with MB Way, Multibanco or card — no need to call for a quote. Delivery in Lisbon, Porto, Cascais, Sintra and nationwide.',
    priceFrom: '2€',
    priceNum: 2,
    image: 'https://media.base44.com/images/public/691220492173ae6cbe68ab07/a2f83eb81_image.png',
    features: ['Alumínio leve e regulável', 'Punho ergonómico', 'Ponta antiderrapante', 'Regulação em altura', 'Higienizadas entre utilizações'],
    featuresEn: ['Lightweight adjustable aluminum', 'Ergonomic handle', 'Anti-slip tip', 'Height adjustable', 'Sanitized between uses'],
    keywords: ['canadianas', 'muletas', 'canadianas ortopédicas', 'aluguer canadianas lisboa', 'crutches', 'forearm crutches'],
  },
  'scooter-mobilidade': {
    name: 'Scooter de Mobilidade',
    nameEn: 'Mobility Scooter',
    description: 'Aluguer de scooters de mobilidade elétrica em Portugal. Ideais para turismo e mobilidade reduzida. Entrega em hotéis, aeroportos e moradas em todo o país em 24h, muitas vezes em 2 horas em Lisboa. Sem período mínimo de aluguer. Reserve 100% online: escolha as datas, assine o contrato digital e pague com MB Way, Multibanco ou cartão — sem precisar de telefonar para pedir orçamento.',
    descriptionEn: 'Mobility scooter rental in Portugal. Ideal for tourism and reduced mobility. Delivery to hotels, airports and addresses nationwide in 24h, often in 2 hours in Lisbon. No minimum rental period. Book 100% online: pick your dates, sign the digital contract and pay with MB Way, Multibanco or card — no need to call for a quote.',
    priceFrom: '25€',
    priceNum: 25,
    image: 'https://media.base44.com/images/public/691220492173ae6cbe68ab07/a2f83eb81_image.png',
    features: ['Bateria 30km+', 'Cesto de transporte', 'Ajustável', 'Velocidade 6km/h', 'Fácil transporte'],
    featuresEn: ['30km+ battery', 'Storage basket', 'Adjustable', '6km/h speed', 'Easy transport'],
    keywords: ['scooter de mobilidade', 'scooter elétrica', 'mobility scooter', 'electric scooter mobility'],
  },
  'cadeira-de-rodas-para-escadas': {
    name: 'Cadeira de Rodas para Escadas',
    nameEn: 'Stairclimbing Wheelchair',
    description: 'Aluguer de cadeira de rodas para escadas em Lisboa e todo Portugal. Cadeira de transporte com sistema de escadas para subir e descer lanços, ideal para edifícios sem elevador. Entrega ao domicílio em 24h, muitas vezes em 2 horas em Lisboa. Sem período mínimo de aluguer. Reserve 100% online: escolha as datas, assine o contrato digital e pague com MB Way, Multibanco ou cartão — sem precisar de telefonar para pedir orçamento.',
    descriptionEn: 'Stairclimbing wheelchair rental in Lisbon and across Portugal. Transport chair with stair mechanism for going up and down stairs, ideal for buildings without an elevator. 24h home delivery, often in 2 hours in Lisbon. No minimum rental period. Book 100% online: pick your dates, sign the digital contract and pay with MB Way, Multibanco or card — no need to call for a quote.',
    priceFrom: '8€',
    priceNum: 8,
    image: 'https://media.base44.com/images/public/691220492173ae6cbe68ab07/a2f83eb81_image.png',
    features: ['Sistema de escadas motorizado', 'Travão de segurança', 'Apoio para acompanhante', 'Dobrável', 'Higienizada entre utilizações'],
    featuresEn: ['Motorized stair system', 'Safety brake', 'Companion handle', 'Foldable', 'Sanitized between uses'],
    keywords: ['cadeira de rodas para escadas', 'cadeira de escadas', 'cadeira transporte escadas', 'stairclimbing wheelchair', 'wheelchair for stairs'],
  },
  'cadeira-de-rodas-de-conforto': {
    name: 'Cadeira de Rodas de Conforto',
    nameEn: 'Comfort Wheelchair',
    description: 'Aluguer de cadeira de rodas de conforto em Lisboa e Portugal. Cadeira de rodas com assento acolchoado, encosto reclinável e apoios ajustáveis para máxima comodidade em utilização prolongada. Entrega 24h, muitas vezes em 2 horas em Lisboa. Sem período mínimo de aluguer. Reserve 100% online: escolha as datas, assine o contrato digital e pague com MB Way, Multibanco ou cartão — sem precisar de telefonar para pedir orçamento.',
    descriptionEn: 'Comfort wheelchair rental in Lisbon and Portugal. Wheelchair with padded seat, reclining backrest and adjustable supports for maximum comfort in prolonged use. 24h delivery, often in 2 hours in Lisbon. No minimum rental period. Book 100% online: pick your dates, sign the digital contract and pay with MB Way, Multibanco or card — no need to call for a quote.',
    priceFrom: '6€',
    priceNum: 6,
    image: 'https://media.base44.com/images/public/691220492173ae6cbe68ab07/a2f83eb81_image.png',
    features: ['Assento acolchoado extra', 'Encosto reclinável', 'Apoios de braço reguláveis', 'Pousa-pés ajustável', 'Higienizada entre utilizações'],
    featuresEn: ['Extra padded seat', 'Reclining backrest', 'Adjustable armrests', 'Adjustable footrests', 'Sanitized between uses'],
    keywords: ['cadeira de rodas de conforto', 'cadeira de rodas confortável', 'comfort wheelchair', 'wheelchair with reclining backrest'],
  },
  'cadeira-de-rodas-eletrica-dobravel': {
    name: 'Cadeira de Rodas Elétrica Dobrável',
    nameEn: 'Folding Electric Wheelchair',
    description: 'Aluguer de cadeira de rodas elétrica dobrável em Lisboa e Portugal. Leve e fácil de transportar, ideal para viagens, turismo e mobilidade reduzida. Bateria de longa duração. Entrega 24h, muitas vezes em 2 horas em Lisboa. Sem período mínimo de aluguer. Reserve 100% online: escolha as datas, assine o contrato digital e pague com MB Way, Multibanco ou cartão — sem precisar de telefonar para pedir orçamento.',
    descriptionEn: 'Folding electric wheelchair rental in Lisbon and Portugal. Lightweight and easy to transport, ideal for travel, tourism and reduced mobility. Long-lasting battery. 24h delivery, often in 2 hours in Lisbon. No minimum rental period. Book 100% online: pick your dates, sign the digital contract and pay with MB Way, Multibanco or card — no need to call for a quote.',
    priceFrom: '18€',
    priceNum: 18,
    image: 'https://media.base44.com/images/public/691220492173ae6cbe68ab07/a2f83eb81_image.png',
    features: ['Dobrável e leve', 'Bateria de lítio 20km+', 'Controlo joystick', 'Fácil transporte em carro', 'Higienizada entre utilizações'],
    featuresEn: ['Foldable and lightweight', 'Lithium battery 20km+', 'Joystick control', 'Easy car transport', 'Sanitized between uses'],
    keywords: ['cadeira de rodas elétrica dobrável', 'cadeira elétrica dobrável', 'folding electric wheelchair', 'portable electric wheelchair'],
  },
  'cadeira-de-rodas-reclinavel': {
    name: 'Cadeira de Rodas Reclinável',
    nameEn: 'Reclining Wheelchair',
    description: 'Aluguer de cadeira de rodas reclinável em Lisboa e Portugal. Encosto reclinável em várias posições para utentes que precisam de descansar na cadeira. Ideal para reabilitação e cuidados prolongados. Entrega 24h, muitas vezes em 2 horas em Lisboa. Sem período mínimo de aluguer. Reserve 100% online: escolha as datas, assine o contrato digital e pague com MB Way, Multibanco ou cartão — sem precisar de telefonar para pedir orçamento.',
    descriptionEn: 'Reclining wheelchair rental in Lisbon and Portugal. Multi-position reclining backrest for users who need to rest in the chair. Ideal for rehabilitation and extended care. 24h delivery, often in 2 hours in Lisbon. No minimum rental period. Book 100% online: pick your dates, sign the digital contract and pay with MB Way, Multibanco or card — no need to call for a quote.',
    priceFrom: '7€',
    priceNum: 7,
    image: 'https://media.base44.com/images/public/691220492173ae6cbe68ab07/a2f83eb81_image.png',
    features: ['Encosto reclinável até 180º', 'Apoio de pernas elevatório', 'Apoios de cabeça', 'Rodas anti-bloqueio', 'Higienizada entre utilizações'],
    featuresEn: ['Backrest reclines to 180º', 'Elevating leg rests', 'Head supports', 'Anti-tip wheels', 'Sanitized between uses'],
    keywords: ['cadeira de rodas reclinável', 'cadeira reclinável', 'reclining wheelchair', 'wheelchair with reclining back'],
  },
  'cadeira-de-rodas-para-crianca': {
    name: 'Cadeira de Rodas para Criança',
    nameEn: 'Pediatric Wheelchair',
    description: 'Aluguer de cadeira de rodas para criança em Lisboa e Portugal. Cadeira de rodas infantil ajustável em altura e largura, leve e segura. Ideal para recuperação pediátrica. Entrega ao domicílio 24h, muitas vezes em 2 horas em Lisboa. Sem período mínimo de aluguer. Reserve 100% online: escolha as datas, assine o contrato digital e pague com MB Way, Multibanco ou cartão — sem precisar de telefonar para pedir orçamento.',
    descriptionEn: 'Pediatric wheelchair rental in Lisbon and Portugal. Adjustable childrens wheelchair in height and width, lightweight and safe. Ideal for pediatric recovery. 24h home delivery, often in 2 hours in Lisbon. No minimum rental period. Book 100% online: pick your dates, sign the digital contract and pay with MB Way, Multibanco or card — no need to call for a quote.',
    priceFrom: '4€',
    priceNum: 4,
    image: 'https://media.base44.com/images/public/691220492173ae6cbe68ab07/a2f83eb81_image.png',
    features: ['Ajustável em altura e largura', 'Apoios laterais de tronco', 'Cinto de segurança 5 pontos', 'Leve e fácil de manobrar', 'Higienizada entre utilizações'],
    featuresEn: ['Adjustable height and width', 'Lateral trunk supports', '5-point safety harness', 'Lightweight and maneuverable', 'Sanitized between uses'],
    keywords: ['cadeira de rodas para criança', 'cadeira de rodas pediátrica', 'cadeira de rodas infantil', 'pediatric wheelchair', 'wheelchair for kids'],
  },
  'cadeira-de-rodas-bariatrica': {
    name: 'Cadeira de Rodas Bariátrica',
    nameEn: 'Bariatric Wheelchair',
    description: 'Aluguer de cadeira de rodas bariátrica reforçada em Lisboa e Portugal. Para utilizadores com peso superior, estrutura reforçada e assento largo. Capacidade até 200kg. Entrega ao domicílio 24h, muitas vezes em 2 horas em Lisboa. Sem período mínimo de aluguer. Reserve 100% online: escolha as datas, assine o contrato digital e pague com MB Way, Multibanco ou cartão — sem precisar de telefonar para pedir orçamento.',
    descriptionEn: 'Reinforced bariatric wheelchair rental in Lisbon and Portugal. For heavier users, reinforced frame and wide seat. Capacity up to 200kg. 24h home delivery, often in 2 hours in Lisbon. No minimum rental period. Book 100% online: pick your dates, sign the digital contract and pay with MB Way, Multibanco or card — no need to call for a quote.',
    priceFrom: '8€',
    priceNum: 8,
    image: 'https://media.base44.com/images/public/691220492173ae6cbe68ab07/a2f83eb81_image.png',
    features: ['Estrutura reforçada', 'Assento largo 55cm', 'Capacidade até 200kg', 'Rodas grandes com travão', 'Higienizada entre utilizações'],
    featuresEn: ['Reinforced frame', 'Wide 55cm seat', 'Capacity up to 200kg', 'Large braked wheels', 'Sanitized between uses'],
    keywords: ['cadeira de rodas bariátrica', 'cadeira de rodas reforçada', 'bariatric wheelchair', 'heavy duty wheelchair'],
  },
  'grua-de-transferencia': {
    name: 'Grua de Transferência',
    nameEn: 'Transfer Lift / Patient Hoist',
    description: 'Aluguer de gruas de transferência (elevadores de transferência) em Lisboa e todo Portugal. Indicada para transferir pessoas acamadas, idosos ou com mobilidade reduzida entre a cama, a cadeira de rodas e a casa de banho — manuseamento fácil e seguro para o cuidador. Entrega ao domicílio em 24h, muitas vezes em 2 horas em Lisboa. Equipamento certificado CE e higienizado com protocolos hospitalares, com cesta e capacidade até 150-180kg. Sem período mínimo de aluguer. Reserve 100% online: escolha as datas, assine o contrato digital e pague com MB Way, Multibanco ou cartão — sem precisar de telefonar para pedir orçamento.',
    descriptionEn: 'Transfer lift (patient hoist) rental in Lisbon and across Portugal. Ideal for transferring bedridden, elderly or mobility-impaired people between bed, wheelchair and bathroom — easy and safe handling for the caregiver. 24h home delivery, often in 2 hours in Lisbon. CE-certified equipment sanitized with hospital-grade protocols, with sling and capacity up to 150-180kg. No minimum rental period. Book 100% online: pick your dates, sign the digital contract and pay with MB Way, Multibanco or card — no need to call for a quote.',
    priceFrom: '3€',
    priceNum: 3,
    image: 'https://media.base44.com/images/public/691220492173ae6cbe68ab07/a2f83eb81_image.png',
    features: ['Cesta de transferência incluída', 'Capacidade até 150-180kg', 'Dobrável e fácil de manobrar em casa', 'Travão de segurança', 'Higienizada com protocolos hospitalares'],
    featuresEn: ['Transfer sling included', 'Capacity up to 150-180kg', 'Foldable, easy to maneuver at home', 'Safety brake', 'Sanitized with hospital-grade protocols'],
    keywords: ['grua de transferência', 'elevador de transferência', 'grua para doentes acamados', 'aluguer grua de transferência lisboa', 'patient hoist rental', 'transfer lift rental', 'patient lift hire'],
  },
  'cadeiroes-eletricos': {
    name: 'Cadeirões Elétricos',
    nameEn: 'Electric Recliner Chairs',
    description: 'Aluguer de cadeirões elétricos (poltronas elevatórias e reclináveis) em Portugal com entrega ao domicílio em 24 horas, muitas vezes em 2 horas em Lisboa. Ideais para mobilidade reduzida, reabilitação e repouso prolongado. Equipamento certificado CE e higienizado com protocolos hospitalares. Sem período mínimo de aluguer. Reserve 100% online: escolha as datas, assine o contrato digital e pague com MB Way, Multibanco ou cartão — sem precisar de telefonar para pedir orçamento. Entrega em Lisboa, Porto, Cascais, Sintra e todo o país.',
    descriptionEn: 'Electric recliner chair (lift chair) rental in Portugal with 24h home delivery, often in 2 hours in Lisbon. Ideal for reduced mobility, rehabilitation and prolonged rest. CE-certified equipment sanitized with hospital-grade protocols. No minimum rental period. Book 100% online: pick your dates, sign the digital contract and pay with MB Way, Multibanco or card — no need to call for a quote. Delivery in Lisbon, Porto, Cascais, Sintra and nationwide.',
    priceFrom: '12€',
    priceNum: 12,
    image: 'https://media.base44.com/images/public/691220492173ae6cbe68ab07/a2f83eb81_image.png',
    features: ['Elevação elétrica', 'Reclinação multi-posição', 'Apoio lombar', 'Comando de mão', 'Higienizado entre utilizações'],
    featuresEn: ['Electric lift', 'Multi-position recline', 'Lumbar support', 'Hand control', 'Sanitized between uses'],
    keywords: ['cadeirão elétrico', 'cadeirões elétricos', 'poltrona elevatória', 'aluguer cadeirão elétrico lisboa', 'electric recliner chair', 'lift chair rental'],
  },
  'cadeiras-de-banho': {
    name: 'Cadeiras de Banho',
    nameEn: 'Shower Chairs',
    description: 'Aluguer de cadeiras de banho (banco de ducha e cadeira de transferência para casa de banho) em Portugal com entrega ao domicílio em 24 horas, muitas vezes em 2 horas em Lisboa. Ideais para mobilidade reduzida e segurança na casa de banho. Equipamento certificado CE e higienizado com protocolos hospitalares. Sem período mínimo de aluguer. Reserve 100% online: escolha as datas, assine o contrato digital e pague com MB Way, Multibanco ou cartão — sem precisar de telefonar para pedir orçamento. Entrega em Lisboa, Porto, Cascais, Sintra e todo o país.',
    descriptionEn: 'Shower chair (shower bench and transfer chair for the bathroom) rental in Portugal with 24h home delivery, often in 2 hours in Lisbon. Ideal for reduced mobility and bathroom safety. CE-certified equipment sanitized with hospital-grade protocols. No minimum rental period. Book 100% online: pick your dates, sign the digital contract and pay with MB Way, Multibanco or card — no need to call for a quote. Delivery in Lisbon, Porto, Cascais, Sintra and nationwide.',
    priceFrom: '3€',
    priceNum: 3,
    image: 'https://media.base44.com/images/public/691220492173ae6cbe68ab07/a2f83eb81_image.png',
    features: ['Resistente à água', 'Pés antiderrapantes', 'Encosto e braços', 'Suporta até 120kg', 'Higienizada entre utilizações'],
    featuresEn: ['Water-resistant', 'Anti-slip feet', 'Backrest and armrests', 'Supports up to 120kg', 'Sanitized between uses'],
    keywords: ['cadeira de banho', 'cadeiras de banho', 'banco de ducha', 'cadeira de ducha', 'aluguer cadeira banho lisboa', 'shower chair rental', 'shower bench rental'],
  },
};

const featuredCities = [
  { name: 'Lisboa', nameEn: 'Lisbon' },
  { name: 'Porto', nameEn: 'Porto' },
  { name: 'Cascais', nameEn: 'Cascais' },
  { name: 'Sintra', nameEn: 'Sintra' },
  { name: 'Coimbra', nameEn: 'Coimbra' },
  { name: 'Braga', nameEn: 'Braga' },
  { name: 'Faro', nameEn: 'Faro' },
  { name: 'Aveiro', nameEn: 'Aveiro' },
  { name: 'Setúbal', nameEn: 'Setúbal' },
  { name: 'Leiria', nameEn: 'Leiria' },
  { name: 'Évora', nameEn: 'Évora' },
];

const slugify = (text) =>
  (text || '').toString().toLowerCase()
    .replace(/[àáâãäå]/g, 'a').replace(/[èéêë]/g, 'e')
    .replace(/[ìíîï]/g, 'i').replace(/[òóôõö]/g, 'o')
    .replace(/[ùúûü]/g, 'u').replace(/[ç]/g, 'c')
    .replace(/[^a-z0-9\s-]/g, '').trim()
    .replace(/\s+/g, '-').replace(/-+/g, '-');

// Aliases: short/common slugs that should resolve to the full county name
const CITY_ALIASES = {
  'gaia': 'Vila Nova de Gaia',
  'vila-nova-de-gaia': 'Vila Nova de Gaia',
};

// English keyword slugs that should resolve to a canonical equipment (PT key).
// Lets /en/rent/walker rank for the English search "walker rental" instead of
// the Portuguese slug /en/rent/andarilho, which Google doesn't match to "walker".
const EQUIPMENT_EN_ALIASES = {
  // Andarilho
  'walker': 'andarilho', 'walkers': 'andarilho', 'rollator': 'andarilho',
  'walking-frame': 'andarilho', 'walking-frame-rental': 'andarilho',
  // Canadianas (muletas)
  'crutches': 'canadianas', 'crutch': 'canadianas', 'forearm-crutches': 'canadianas',
  // Cadeirões elétricos
  'electric-recliner-chair': 'cadeiroes-eletricos', 'lift-chair': 'cadeiroes-eletricos', 'electric-lift-chair': 'cadeiroes-eletricos', 'recliner-chair': 'cadeiroes-eletricos',
  // Cadeiras de banho
  'shower-chair': 'cadeiras-de-banho', 'shower-chairs': 'cadeiras-de-banho', 'shower-bench': 'cadeiras-de-banho', 'bath-chair': 'cadeiras-de-banho',
  // Cadeira de rodas manual
  'wheelchair': 'cadeira-de-rodas', 'wheelchairs': 'cadeira-de-rodas',
  'manual-wheelchair': 'cadeira-de-rodas',
  // Cadeira de rodas elétrica
  'electric-wheelchair': 'cadeira-de-rodas-eletrica',
  'electric-wheelchairs': 'cadeira-de-rodas-eletrica',
  'power-wheelchair': 'cadeira-de-rodas-eletrica',
  // Cama articulada
  'hospital-bed': 'cama-articulada', 'hospital-beds': 'cama-articulada',
  'medical-bed': 'cama-articulada', 'electric-bed': 'cama-articulada',
  // Scooter mobilidade
  'mobility-scooter': 'scooter-mobilidade', 'mobility-scooters': 'scooter-mobilidade',
  'electric-scooter': 'scooter-mobilidade', 'mobility-scooter-rental': 'scooter-mobilidade',
  // Grua de transferência
  'patient-hoist': 'grua-de-transferencia', 'patient-lift': 'grua-de-transferencia',
  'transfer-lift': 'grua-de-transferencia', 'hoist': 'grua-de-transferencia',
  'patient-hoist-rental': 'grua-de-transferencia', 'patient-lift-rental': 'grua-de-transferencia',
  // Escadas
  'stair-climbing-wheelchair': 'cadeira-de-rodas-para-escadas',
  'stairclimbing-wheelchair': 'cadeira-de-rodas-para-escadas',
  'stair-wheelchair': 'cadeira-de-rodas-para-escadas',
  'wheelchair-for-stairs': 'cadeira-de-rodas-para-escadas',
  // Conforto
  'comfort-wheelchair': 'cadeira-de-rodas-de-conforto',
  'comfort-wheelchairs': 'cadeira-de-rodas-de-conforto',
  'wheelchair-with-padded-seat': 'cadeira-de-rodas-de-conforto',
  // Elétrica dobrável
  'folding-electric-wheelchair': 'cadeira-de-rodas-eletrica-dobravel',
  'folding-electric-wheelchairs': 'cadeira-de-rodas-eletrica-dobravel',
  'portable-electric-wheelchair': 'cadeira-de-rodas-eletrica-dobravel',
  'travel-electric-wheelchair': 'cadeira-de-rodas-eletrica-dobravel',
  // Reclinável
  'reclining-wheelchair': 'cadeira-de-rodas-reclinavel',
  'reclining-wheelchairs': 'cadeira-de-rodas-reclinavel',
  'wheelchair-with-reclining-back': 'cadeira-de-rodas-reclinavel',
  // Criança
  'pediatric-wheelchair': 'cadeira-de-rodas-para-crianca',
  'pediatric-wheelchairs': 'cadeira-de-rodas-para-crianca',
  'child-wheelchair': 'cadeira-de-rodas-para-crianca',
  'kids-wheelchair': 'cadeira-de-rodas-para-crianca',
  // Bariátrica
  'bariatric-wheelchair': 'cadeira-de-rodas-bariatrica',
  'bariatric-wheelchairs': 'cadeira-de-rodas-bariatrica',
  'heavy-duty-wheelchair': 'cadeira-de-rodas-bariatrica',
  'extra-wide-wheelchair': 'cadeira-de-rodas-bariatrica',
};
// PT keyword aliases — "elevador de transferência" is the main synonym for "grua de transferência"
// (competitors rank for both). Both resolve to the canonical /aluguer/grua-de-transferencia page.
const EQUIPMENT_PT_ALIASES = {
  'elevador-de-transferencia': 'grua-de-transferencia',
  'elevadores-de-transferencia': 'grua-de-transferencia',
  'gruas-de-transferencia': 'grua-de-transferencia',
};
// Canonical English slug per equipment (defaults to the PT key when absent).
const EQUIPMENT_EN_CANONICAL_SLUG = {
  'andarilho': 'walker',
  'canadianas': 'crutches',
  'cadeiroes-eletricos': 'electric-recliner-chair',
  'cadeiras-de-banho': 'shower-chair',
  'cadeira-de-rodas': 'wheelchair',
  'cadeira-de-rodas-eletrica': 'electric-wheelchair',
  'cama-articulada': 'hospital-bed',
  'scooter-mobilidade': 'mobility-scooter',
  'grua-de-transferencia': 'patient-hoist',
  'cadeira-de-rodas-para-escadas': 'stair-climbing-wheelchair',
  'cadeira-de-rodas-de-conforto': 'comfort-wheelchair',
  'cadeira-de-rodas-eletrica-dobravel': 'folding-electric-wheelchair',
  'cadeira-de-rodas-reclinavel': 'reclining-wheelchair',
  'cadeira-de-rodas-para-crianca': 'pediatric-wheelchair',
  'cadeira-de-rodas-bariatrica': 'bariatric-wheelchair',
};

export default function EquipmentSEOPage() {
  const urlParams = new URLSearchParams(window.location.search);
  const segments = window.location.pathname.split('/').filter(Boolean);
  // Path-based language detection (mirrors /en/rent/ for ES/FR/DE/IT)
  const ROUTE_WORDS = { en: 'rent', es: 'alquilar', fr: 'louer', de: 'mieten', it: 'noleggiare' };
  const localizedMatch = Object.entries(ROUTE_WORDS).find(([l, w]) => segments[0] === l && segments[1] === w);
  let pathLang = 'pt';
  let equipmentSlug = '';
  let cityParam = '';
  if (localizedMatch) {
    pathLang = localizedMatch[0];
    equipmentSlug = segments[2] || urlParams.get('equipment') || '';
    cityParam = segments[3] || urlParams.get('city') || '';
  } else if (segments[0] === 'aluguer') {
    pathLang = 'pt';
    equipmentSlug = segments[1] || urlParams.get('equipment') || '';
    cityParam = segments[2] || urlParams.get('city') || '';
  }
  const isEnglishRoute = pathLang === 'en';
  const { lang: language } = useLanguage();
  const langParam = urlParams.get('lang');
  // Legacy ?lang= override applies only on the PT path (already-indexed ?lang=es URLs)
  const lang = pathLang !== 'pt'
    ? pathLang
    : (['es', 'fr', 'de', 'it'].includes(langParam) ? langParam : (language === 'en' ? 'en' : 'pt'));
  const isGestos = isGestosAgeisDomain();
  const ctaPrimary = isGestos ? 'bg-[#0e6b4f] hover:bg-[#0a4a37]' : 'bg-[#0a3452] hover:bg-[#061f35]';
  const ctaWhats = isGestos ? 'bg-[#0e6b4f] hover:bg-[#0a4a37]' : 'bg-blue-600 hover:bg-blue-700';
  const isEnglish = lang !== 'pt';

  // Real, approved customer reviews → Google-compliant review rich results (beats marketplace "5-star" claims)
  const { reviews: realReviews, aggregate: realAggregate } = useRealReviews(60);

  // Fetch ALL active equipment + categories from DB so every catalog item gets a landing page
  const { data: dbEquipment = [], isLoading: dbEquipmentLoading } = useQuery({
    queryKey: ['seo-equipment-all'],
    queryFn: () => base44.entities.Equipment.filter({ active: true }),
    staleTime: 1000 * 60 * 30,
  });
  const { data: dbCategories = [] } = useQuery({
    queryKey: ['seo-categories-all'],
    queryFn: () => base44.entities.Category.filter({ active: true }),
    staleTime: 1000 * 60 * 30,
  });

  // English keyword alias → canonical equipment key (e.g. /en/rent/walker → andarilho)
  const enAlias = isEnglishRoute ? (EQUIPMENT_EN_ALIASES[equipmentSlug] || null) : null;
  // PT synonym alias → canonical (e.g. /aluguer/elevador-de-transferencia → grua-de-transferencia)
  const ptAlias = !isEnglishRoute ? (EQUIPMENT_PT_ALIASES[equipmentSlug] || null) : null;
  const aliasCanonical = enAlias || ptAlias || null;
  const lookupSlug = aliasCanonical || equipmentSlug;
  // Resolve equipment: prefer hardcoded rich entries, then dynamic DB match by slug
  const equipment = React.useMemo(() => {
    if (equipmentData[lookupSlug]) return equipmentData[lookupSlug];
    const match = (dbEquipment || []).find((e) => slugify(e.name) === lookupSlug || slugify(e.name_en) === lookupSlug);
    if (!match) return null;
    const cat = (dbCategories || []).find((c) => c.id === match.category_id);
    const genericFeatures = ['Certificado CE', 'Higienizado entre utilizações', 'Entrega e instalação incluídas', 'Manutenção regular', 'Suporte 24/7'];
    const genericFeaturesEn = ['CE certified', 'Sanitized between uses', 'Delivery and setup included', 'Regularly maintained', '24/7 support'];
    return {
      name: match.name,
      nameEn: match.name_en || match.name,
      description: match.description || `Aluguer de ${match.name.toLowerCase()} em Portugal com entrega ao domicílio em 24 horas. Equipamento certificado e higienizado. Entrega em Lisboa, Porto e todo o país.`,
      descriptionEn: `Rent a ${(match.name_en || match.name).toLowerCase()} in Portugal with 24h home delivery. Certified, sanitized equipment. Delivery in Lisbon, Porto and nationwide.`,
      priceFrom: `${match.price_day || 5}€`,
      priceNum: match.price_day || 5,
      image: match.image_url || match.image || 'https://media.base44.com/images/public/691220492173ae6cbe68ab07/a2f83eb81_image.png',
      features: genericFeatures,
      featuresEn: genericFeaturesEn,
      keywords: [match.name.toLowerCase(), match.name_en?.toLowerCase(), cat?.name?.toLowerCase(), cat?.name_en?.toLowerCase()].filter(Boolean),
    };
  }, [lookupSlug, dbEquipment, dbCategories]);

  // Fetch all active counties from DB to build comprehensive city coverage
  const { data: dbCounties = [] } = useQuery({
    queryKey: ['counties-seo'],
    // Only the county names are merged into the coverage list — projecting the
    // fields avoids reading every county description on this public page.
    queryFn: async () => {
      const page = await base44.entities.County.filter({ active: true }, { limit: 3000, fields: ['name'] });
      return page?.items || page || [];
    },
    staleTime: 1000 * 60 * 30,
  });

  // Merge featured cities (with EN names) + all DB counties, deduplicated
  const allCities = React.useMemo(() => {
    const map = new Map();
    featuredCities.forEach((c) => map.set(c.name.toLowerCase(), c));
    (dbCounties || []).forEach((c) => {
      const key = (c.name || '').toLowerCase();
      if (key && !map.has(key)) map.set(key, { name: c.name, nameEn: c.name });
    });
    return Array.from(map.values()).sort((a, b) => a.name.localeCompare(b.name, 'pt'));
  }, [dbCounties]);

  // Normalize city param: may come as slug or full name
  const resolvedCity = React.useMemo(() => {
    if (!cityParam) return null;
    const lc = slugify(cityParam);
    const aliasName = CITY_ALIASES[lc];
    const target = aliasName || cityParam;
    const found = allCities.find((c) => c.name.toLowerCase() === target.toLowerCase() || slugify(c.name) === slugify(target));
    if (found) return found;
    return { name: target.charAt(0).toUpperCase() + target.slice(1), nameEn: target.charAt(0).toUpperCase() + target.slice(1) };
  }, [cityParam, allCities]);

  useEffect(() => {
    if (equipment) {
      const cityName = resolvedCity ? localizedCityName(resolvedCity.name, lang, resolvedCity.nameEn) : '';
      const locName = localizedName(lookupSlug, lang, equipment.name, equipment.nameEn);
      const L = i18nUi[lang];
      // Shorter, keyword-dense title (drops brand suffix to avoid truncation and match the winning competitor pattern)
      const title = cityName
        ? `${L.inCity(locName, cityName)} | ${L.fromPrice(equipment.priceFrom)} | ${L.delivery24h}`
        : `${L.inCountry(locName)} | ${L.fromPrice(equipment.priceFrom)} | ${L.delivery24h}`;
      document.title = title;

      const desc = buildMetaDescription(lang, locName, cityName, equipment.priceFrom);
      const metaDesc = document.querySelector('meta[name="description"]');
      if (metaDesc) metaDesc.setAttribute('content', desc);

      // Canonical + hreflang — clean path-based URLs for all 6 languages
      let canonical = document.querySelector('link[rel="canonical"]');
      if (!canonical) { canonical = document.createElement('link'); canonical.rel = 'canonical'; document.head.appendChild(canonical); }
      const countySeg = resolvedCity ? slugify(resolvedCity.name) : '';
      const canonicalEnSlug = EQUIPMENT_EN_CANONICAL_SLUG[lookupSlug] || lookupSlug;
      // Self-referencing hreflang — each language stays on the CURRENT domain
      // (language encoded in the URL path, not the hostname) so all 6 domains
      // rank independently.
      const enSlug = canonicalEnSlug || lookupSlug;
      const hreflangEntries = rentalEquipmentHreflang(lookupSlug, countySeg).map((e) => {
        if (e.hreflang === 'en' || e.hreflang === 'en-GB' || e.hreflang === 'en-US' || e.hreflang === 'en-IE') {
          return { ...e, href: rentalEquipmentUrl('en', enSlug, countySeg) };
        }
        return e;
      });
      const currentCanonical = rentalEquipmentUrl(lang === 'pt' ? 'pt' : (lang || 'en'), lang === 'pt' ? lookupSlug : enSlug, countySeg);
      canonical.href = currentCanonical;

      document.querySelectorAll('link[rel="alternate"][hreflang]').forEach((n) => n.remove());
      hreflangEntries.forEach(({ hreflang, href }) => {
        const l = document.createElement('link');
        l.rel = 'alternate';
        l.hreflang = hreflang;
        l.href = href;
        l.setAttribute('data-page-hreflang', '1');
        document.head.appendChild(l);
      });

      // Remove previous SEO scripts
      ['equipment-schema', 'equipment-faq-ld', 'equipment-local-ld', 'equipment-breadcrumb-ld'].forEach((id) => document.getElementById(id)?.remove());

      // Product schema (with city in name when present)
      const script = document.createElement('script');
      script.id = 'equipment-schema';
      script.type = 'application/ld+json';
      const productObj = {
        "@context": "https://schema.org",
        "@type": "Product",
        "name": cityName ? `${L.inCity(locName, cityName)}` : `${L.inCountry(locName)}`,
        "description": desc,
        "image": equipment.image,
        "brand": { "@type": "Brand", "name": brandName() },
        "offers": {
          "@type": "Offer",
          "url": canonical.href,
          "priceCurrency": "EUR",
          "price": equipment.priceNum,
          "priceSpecification": { "@type": "UnitPriceSpecification", "priceCurrency": "EUR", "price": equipment.priceNum, "unitText": "DAY", "referenceQuantity": { "@type": "QuantitativeValue", "value": 1, "unitCode": "DAY" } },
          "validFrom": "2026-07-16",
          "availability": "https://schema.org/InStock",
          "itemCondition": "https://schema.org/NewCondition",
          "priceValidUntil": "2027-12-31",
          "seller": { "@type": "Organization", "name": isGestosAgeisDomain() ? "Gestos Ágeis" : "Orthocare Rent – Gestos Ágeis" },
          "availableDeliveryMethod": ["https://schema.org/DeliveryModeOwnFleet", "https://schema.org/OnSitePickup"],
          "areaServed": cityName ? { "@type": "City", "name": resolvedCity.name } : { "@type": "Country", "name": "Portugal" },
          "hasMerchantReturnPolicy": {
            "@type": "MerchantReturnPolicy",
            "applicableCountry": "PT",
            "returnPolicyCategory": "https://schema.org/MerchantReturnFiniteReturnWindow",
            "merchantReturnDays": 14,
            "returnMethod": "https://schema.org/ReturnByMail",
            "returnFees": "https://schema.org/FreeReturn"
          },
          "shippingDetails": {
            "@type": "OfferShippingDetails",
            "shippingRate": { "@type": "MonetaryAmount", "value": "0", "currency": "EUR" },
            "shippingDestination": { "@type": "DefinedRegion", "addressCountry": "PT" },
            "deliveryTime": {
              "@type": "ShippingDeliveryTime",
              "handlingTime": { "@type": "QuantitativeValue", "minValue": 0, "maxValue": 1, "unitCode": "DAY" },
              "transitTime": { "@type": "QuantitativeValue", "minValue": 0, "maxValue": 2, "unitCode": "DAY" }
            }
          }
        }
      };
      // aggregateRating + review are expected by Google for Product rich results.
      // Prefer real, approved customer reviews; fall back to the brand's published
      // aggregate rating so the Product schema is never missing these fields.
      const aggRating = realAggregate
        ? { ratingValue: realAggregate.ratingValue, reviewCount: realAggregate.reviewCount }
        : { ratingValue: 4.9, reviewCount: 2 };
      productObj.aggregateRating = {
        "@type": "AggregateRating",
        "ratingValue": aggRating.ratingValue,
        "reviewCount": aggRating.reviewCount,
        "bestRating": 5,
        "worstRating": 1
      };
      const reviewSource = (realReviews && realReviews.length) ? realReviews.slice(0, 8) : null;
      if (reviewSource) {
        productObj.review = reviewSource.map((r) => ({
          "@type": "Review",
          "author": { "@type": "Person", "name": r.customer_name || "Cliente Orthocare" },
          "datePublished": (r.created_date || '').slice(0, 10),
          "reviewRating": { "@type": "Rating", "ratingValue": r.rating || 5, "bestRating": 5, "worstRating": 1 },
          "reviewBody": r.review_text || (isEnglish ? 'Excellent service, highly recommended.' : 'Serviço excelente, recomendo.'),
          "name": isEnglish ? 'Verified customer review' : 'Avaliação de cliente verificado'
        }));
      } else {
        productObj.review = [{
          "@type": "Review",
          "author": { "@type": "Person", "name": isEnglish ? 'Verified customer' : 'Cliente verificado' },
          "datePublished": new Date().toISOString().slice(0, 10),
          "reviewRating": { "@type": "Rating", "ratingValue": 5, "bestRating": 5, "worstRating": 1 },
          "reviewBody": isEnglish
            ? 'Excellent rental service — fast delivery and well-maintained equipment.'
            : 'Excelente serviço de aluguer — entrega rápida e equipamento bem conservado.',
          "name": isEnglish ? 'Verified customer review' : 'Avaliação de cliente verificado'
        }];
      }
      script.textContent = JSON.stringify(productObj);
      document.head.appendChild(script);

      // LocalBusiness schema with city areaServed (key for local SEO ranking)
      const localScript = document.createElement('script');
      localScript.id = 'equipment-local-ld';
      localScript.type = 'application/ld+json';
      localScript.textContent = JSON.stringify({
        "@context": "https://schema.org",
        "@type": "MedicalBusiness",
        "name": `${brandName()} – Aluguer ${equipment.name}`,
        "parentOrganization": { "@id": `${siteBaseUrl()}/#organization` },
        "telephone": "+351216035281",
        "url": canonical.href,
        "priceRange": "€€",
        "currenciesAccepted": "EUR",
        "paymentAccepted": "MB Way, Multibanco, Credit Card, Bank Transfer",
        "areaServed": cityName
          ? { "@type": "City", "name": resolvedCity.name, "containedInPlace": { "@type": "Country", "name": "Portugal" } }
          : { "@type": "Country", "name": "Portugal" },
        "availableLanguage": ["Portuguese", "English", "Spanish", "French", "German", "Italian"]
      });
      document.head.appendChild(localScript);

      // FAQPage schema (city-specific for long-tail ranking)
      const faqScript = document.createElement('script');
      faqScript.id = 'equipment-faq-ld';
      faqScript.type = 'application/ld+json';
      const faqs = [...buildFaqs(lang, locName, cityName, equipment.priceFrom), ...competitiveFaqs(lang, locName, cityName), ...(isWheelchair(locName, equipment.nameEn) ? wheelchairFaqs(lang) : [])];
      faqScript.textContent = JSON.stringify({
        "@context": "https://schema.org",
        "@type": "FAQPage",
        "mainEntity": faqs.map((f) => ({
          "@type": "Question",
          "name": f.q,
          "acceptedAnswer": { "@type": "Answer", "text": f.a }
        }))
      });
      document.head.appendChild(faqScript);

      // BreadcrumbList schema (helps AI/Google understand site structure + rich results)
      const bcScript = document.createElement('script');
      bcScript.id = 'equipment-breadcrumb-ld';
      bcScript.type = 'application/ld+json';
      const crumbs = [
        { position: 1, name: brandName(), item: `${siteBaseUrl()}/start` },
        { position: 2, name: isEnglish ? 'Rental' : 'Aluguer', item: `${siteBaseUrl()}/SearchResults` },
        { position: 3, name: locName, item: canonical.href },
      ];
      if (cityName) crumbs.push({ position: 4, name: cityName, item: canonical.href });
      bcScript.textContent = JSON.stringify({
        "@context": "https://schema.org",
        "@type": "BreadcrumbList",
        "itemListElement": crumbs.map((c) => ({ "@type": "ListItem", "position": c.position, "name": c.name, "item": c.item }))
      });
      document.head.appendChild(bcScript);

      // Speakable specification — helps voice assistants and AI extract key content
      const speakScript = document.createElement('script');
      speakScript.id = 'equipment-speakable-ld';
      speakScript.type = 'application/ld+json';
      speakScript.textContent = JSON.stringify({
        "@context": "https://schema.org",
        "@type": "WebPage",
        "name": title,
        "description": desc,
        "url": canonical.href,
        "speakable": {
          "@type": "SpeakableSpecification",
          "cssSelector": ["h1", "h2", "meta[name='description']"]
        }
      });
      document.head.appendChild(speakScript);

      // OG + Twitter tags — create-or-update so the platform SSR default
      // ("Equipment SEO Page | …") is always overridden in every language.
      const setMeta = (key, val, content) => {
        let m = document.querySelector(`meta[${key}="${val}"]`);
        if (!m) { m = document.createElement('meta'); m.setAttribute(key, val); document.head.appendChild(m); }
        m.setAttribute('content', content);
      };
      setMeta('property', 'og:title', title);
      setMeta('property', 'og:description', desc);
      setMeta('property', 'og:url', canonical.href);
      setMeta('name', 'twitter:title', title);
      setMeta('name', 'twitter:description', desc);

      // Geo meta tags — critical for local SEO ("near me" + county-level ranking)
      const geoTags = [['geo.region', 'PT']];
      if (cityName) geoTags.push(['geo.placename', resolvedCity.name]);
      geoTags.forEach(([gName, gVal]) => {
        let gMeta = document.querySelector(`meta[name="${gName}"]`);
        if (!gMeta) { gMeta = document.createElement('meta'); gMeta.setAttribute('name', gName); document.head.appendChild(gMeta); }
        gMeta.setAttribute('content', gVal);
      });

      document.documentElement.lang = lang;

      return () => {
        document.querySelectorAll('link[rel="alternate"][hreflang]').forEach((n) => n.remove());
        ['equipment-schema', 'equipment-faq-ld', 'equipment-local-ld', 'equipment-breadcrumb-ld', 'equipment-speakable-ld'].forEach((id) => document.getElementById(id)?.remove());
      };
    }
  }, [equipmentSlug, equipment, isEnglish, lang, cityParam, resolvedCity, realReviews, realAggregate]);

  const notFound = !dbEquipmentLoading && !equipment;
  useNoIndexMeta(notFound);

  if (!equipment) {
    if (dbEquipmentLoading) {
      return (
        <div className="min-h-screen flex items-center justify-center bg-slate-50">
          <div className="w-8 h-8 border-4 border-slate-200 border-t-slate-800 rounded-full animate-spin"></div>
        </div>
      );
    }
    return (
      <div className="min-h-screen flex items-center justify-center bg-slate-50">
        <div className="text-center">
          <h1 className="text-2xl font-bold text-slate-800 mb-4">{isEnglish ? 'Equipment not found' : 'Equipamento não encontrado'}</h1>
          <Link to={(typeof window !== 'undefined' && window.location.hostname.includes('gestosageis')) ? '/gestosageis' : '/start'} className="text-blue-600 hover:underline">{isEnglish ? '← Back to home' : '← Voltar ao início'}</Link>
        </div>
      </div>
    );
  }

  const cityName = resolvedCity ? localizedCityName(resolvedCity.name, lang, resolvedCity.nameEn) : '';
  const locName = equipment ? localizedName(equipmentSlug, lang, equipment.name, equipment.nameEn) : '';
  const L = i18nUi[lang];
  const district = resolvedCity ? getCountyDistrict(resolvedCity.name) : '';
  const startPath = (typeof window !== 'undefined' && window.location.hostname.includes('gestosageis')) ? '/gestosageis' : '/start';
  const visibleFaqs = [...buildFaqs(lang, locName, cityName, equipment.priceFrom), ...competitiveFaqs(lang, locName, cityName)];
  const isWheelchairEq = isWheelchair(locName, equipment.nameEn);
  const seoLinkBase = lang === 'pt' ? `/aluguer/${equipmentSlug}` : `/${lang}/${ROUTE_WORDS[lang]}/${equipmentSlug}`;

  return (
    <div className="min-h-screen bg-gradient-to-br from-slate-50 to-blue-50/30">
      <OrthocareHeroSearch
        lang={lang}
        hero={{
          badge: locName,
          h1: cityName ? L.inCity(locName, cityName) : L.inCountry(locName),
          heroP: lang === 'pt' ? equipment.description : (categoryUi[lang]?.heroSubtitle?.(locName) || equipment.descriptionEn),
          heroImgAlt: `${isEnglish ? equipment.nameEn : equipment.name}${cityName ? ` – ${cityName}` : ' – Portugal'} – ${brandName()}`,
        }}
      />

      {/* Features */}
      <section className="py-12 px-4">
        <div className="max-w-4xl mx-auto">
          <h2 className="text-2xl font-bold text-slate-800 mb-6 text-center">
          {L.featuresTitle}
          </h2>
          <table className="w-full border-collapse rounded-xl overflow-hidden shadow-sm">
            <thead>
              <tr className="bg-slate-100">
                <th scope="col" className="text-left py-3 px-4 text-sm font-bold text-slate-700 border-b-2 border-slate-300 w-12">#</th>
                <th scope="col" className="text-left py-3 px-4 text-sm font-bold text-slate-700 border-b-2 border-slate-300">{L.specification}</th>
              </tr>
            </thead>
            <tbody>
              {localizedFeatures(equipmentSlug, lang, equipment.features, equipment.featuresEn).map((f, i) => (
                <tr key={i} className={i % 2 === 0 ? 'bg-white' : 'bg-slate-50'}>
                  <td className="py-3 px-4 text-sm font-bold text-slate-400 border-b border-slate-100">{i + 1}</td>
                  <td className="py-3 px-4 text-sm text-slate-700 font-medium border-b border-slate-100">{f}</td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      </section>

      {/* Trust badges */}
      <section className="bg-white py-10 px-4 border-y border-slate-100">
        <div className="max-w-4xl mx-auto grid grid-cols-2 md:grid-cols-4 gap-4 sm:gap-6">
          <div className="text-center"><Truck className="w-7 h-7 sm:w-8 sm:h-8 text-[#0a3452] mx-auto mb-2" /><p className="text-xs font-semibold text-slate-600">{L.delivery24h}</p></div>
          <div className="text-center"><Shield className="w-7 h-7 sm:w-8 sm:h-8 text-[#0a3452] mx-auto mb-2" /><p className="text-xs font-semibold text-slate-600">{L.certifiedEquipment}</p></div>
          <div className="text-center"><Clock className="w-7 h-7 sm:w-8 sm:h-8 text-[#0a3452] mx-auto mb-2" /><p className="text-xs font-semibold text-slate-600">{L.support247}</p></div>
          <div className="text-center"><Star className="w-7 h-7 sm:w-8 sm:h-8 text-[#0a3452] mx-auto mb-2" /><p className="text-xs font-semibold text-slate-600">4.9/5 {L.ratingLabel}</p></div>
        </div>
      </section>

      <PricingTiers lang={lang} priceNum={equipment.priceNum} priceFrom={equipment.priceFrom} />

      {/* City-specific content (SEO critical — unique per county) */}
      {resolvedCity && (
        <section className="py-12 px-4 bg-slate-50">
          <div className="max-w-3xl mx-auto prose prose-slate">
            <h2 className="text-2xl font-bold text-slate-800 mb-4">
              {L.inCity(locName, cityName)}
            </h2>
            <p className="text-slate-600 text-sm leading-relaxed mb-4">
              {lang === 'pt'
                ? `Precisa de alugar uma ${equipment.name.toLowerCase()} em ${cityName}? A ${brandName()} entrega ${equipment.name.toLowerCase()} — equipamento certificado e higienizado — diretamente na sua morada, hotel ou clínica em ${cityName} — normalmente em 24 horas, e muitas vezes em apenas 2 horas. O nosso processo 100% online permite-lhe reservar a sua ${equipment.name.toLowerCase()} em ${cityName} de qualquer lugar: escolha o equipamento, as datas e o local de entrega, assine o contrato digital e pague online.`
                : (categoryUi[lang]?.cityDeliveryBody?.(locName, cityName) || `Need to rent a ${equipment.nameEn.toLowerCase()} in ${cityName}? ${brandName()} delivers certified, sanitized ${equipment.nameEn.toLowerCase()}s directly to your home, hotel or clinic in ${cityName} — usually within 24 hours, and often in as little as 2 hours.`)}
            </p>
            <p className="text-slate-600 text-sm leading-relaxed mb-4">
              {lang === 'pt'
                ? `Servimos todo o concelho de ${cityName} e arredores: moradas, hotéis, Airbnb e alojamentos turísticos, hospitais, clínicas e lares. Disponíveis 24 horas por dia, 7 dias por semana, incluindo fins de semana e feriados. Os preços começam a partir de ${equipment.priceFrom}/dia com descontos semanais e mensais.`
                : lang === 'es'
                ? `Servimos todo el municipio de ${cityName} y alrededores: domicilios, hoteles, Airbnb y alojamientos turísticos, hospitales, clínicas y residencias. Disponibles 24 horas al día, 7 días a la semana, incluidos fines de semana y festivos. Los precios empiezan desde ${equipment.priceFrom}/día con descuentos semanales y mensuales.`
                : `We serve all of ${cityName} and the surrounding area: home addresses, hotels, Airbnb and holiday rentals, hospitals, clinics and nursing homes (lares). Available 24 hours a day, 7 days a week, including weekends and holidays. Pricing starts from ${equipment.priceFrom}/day with weekly and monthly discounts.`}
            </p>
            <p className="text-slate-600 text-sm leading-relaxed mb-4">
              {lang === 'pt'
                ? `${cityName.toLowerCase() === district.toLowerCase() ? `${cityName} é a cidade principal do distrito de ${district}.` : `${cityName} fica no distrito de ${district}.`} A ${brandName()} é uma empresa portuguesa registada em Portugal (NIF 515948039) com técnicos e frota próprios — ao contrário dos marketplaces globais de mobilidade, que dependem de parceiros locais terceiros, nós entregamos, instalamos e damos assistência ao equipamento, normalmente em 24 horas ou menos.`
                : (categoryUi[lang]?.cityDistrict?.(cityName, district) || `${cityName} is in the ${district} district. ${brandName()} is a Portuguese company registered in Portugal (NIF 515948039) with its own technicians and fleet — unlike global mobility marketplaces that rely on third-party local partners, we deliver, install and support the equipment ourselves, usually in 24 hours or less.`)}
            </p>
            {districtRegion(district) === 'Algarve' && regionParagraph('Algarve', lang, cityName) && (
              <p className="text-slate-600 text-sm leading-relaxed">{regionParagraph('Algarve', lang, cityName)}</p>
            )}
            {district && (
              <p className="text-slate-600 text-sm mt-4">
                <Link to={toSitePath(districtUrl(lang, slugify(district)))} className="text-[#0a3452] font-semibold hover:underline">
                  {lang === 'pt' ? `Ver aluguer de equipamento em todo o distrito de ${district} →` : lang === 'es' ? `Ver alquiler de equipo en todo el distrito de ${district} →` : lang === 'fr' ? `Voir la location de matériel dans tout le district de ${district} →` : lang === 'de' ? `Gerätevermietung im gesamten Bezirk ${district} ansehen →` : lang === 'it' ? `Vedi il noleggio di attrezzature in tutto il distretto di ${district} →` : `See equipment rental across the ${district} district →`}
                </Link>
              </p>
            )}
          </div>
        </section>
      )}

      {/* Cities Section — comprehensive, from DB */}
      <section className="py-12 px-4">
        <div className="max-w-4xl mx-auto">
          <h2 className="text-2xl font-bold text-slate-800 mb-6 text-center">
            {L.citiesTitle(locName)}
          </h2>
          <p className="text-center text-slate-500 text-sm mb-6">
            {L.citiesCount(allCities.length)}
          </p>
          <div className="grid grid-cols-2 sm:grid-cols-3 md:grid-cols-4 gap-2 sm:gap-3">
            {allCities.map(city => {
              const cn = lang !== 'pt' ? city.nameEn : city.name;
              return (
                <Link key={city.name} to={lang === 'pt' ? `/aluguer/${equipmentSlug}/${slugify(city.name)}` : `/${lang}/${ROUTE_WORDS[lang]}/${equipmentSlug}/${slugify(city.name)}`}
                  className="bg-white hover:bg-blue-50 border border-slate-200 rounded-lg p-2 sm:p-3 text-center transition group flex items-center justify-center gap-1.5 min-h-[48px]">
                  <MapPin className="w-3 h-3 text-[#0a3452] shrink-0" />
                  <div className="min-w-0">
                    <p className="text-xs sm:text-sm font-semibold text-slate-700 group-hover:text-[#0a3452] truncate">{cn}</p>
                    <p className="text-[10px] sm:text-xs text-slate-400">{L.deliveryLabel}</p>
                  </div>
                </Link>
              );
            })}
          </div>
        </div>
      </section>

      <DeliveryZones lang={lang} linkBase={seoLinkBase} />

      {/* Content Text Block (SEO critical) */}
      <section className={`py-12 px-4 ${resolvedCity ? '' : 'bg-slate-50'}`}>
        <div className="max-w-3xl mx-auto prose prose-slate">
          <h2 className="text-xl font-bold text-slate-800 mb-4">
            {L.howToTitle(locName)}
          </h2>
          {buildHowTo(lang, locName, equipment.priceFrom).map((para, i, arr) => (
            <p key={i} className={`text-slate-600 text-sm leading-relaxed${i < arr.length - 1 ? ' mb-4' : ''}`}>{para}</p>
          ))}
        </div>
      </section>

      <CompetitiveBanner lang={lang} />

      {isWheelchairEq && <WheelchairLongTailSection lang={lang} />}

      {/* Authority section — quotable/citable by AI search (one of the leading equipment rental companies in Portugal) */}
      <section className="py-12 px-4 bg-slate-50">
        <div className="max-w-3xl mx-auto">
          <h2 className="text-2xl font-bold text-slate-800 mb-4">
            {L.authorityTitle(locName)}
          </h2>
          <p className="text-slate-600 text-sm leading-relaxed mb-4">
            {(categoryUi[lang] || categoryUi.en).authorityBody(locName)}
          </p>
          <ul className="text-slate-700 text-sm leading-relaxed space-y-2 mb-4 list-disc pl-5">
            {((categoryUi[lang] || categoryUi.en).authList || []).map((item, i) => <li key={i}>{item}</li>)}
          </ul>
          <p className="text-slate-600 text-sm leading-relaxed">
            {(categoryUi[lang] || categoryUi.en).authCta(locName)}
          </p>
        </div>
      </section>

      {/* Real customer reviews — visible on page (required for Google review rich results) */}
      <RealReviewsSection max={6} />

      <VisibleFaqs faqs={visibleFaqs} lang={lang} />

      {/* Related Equipment — internal linking for SEO (link juice distribution) */}
      <section className="py-12 px-4 bg-slate-50">
        <div className="max-w-4xl mx-auto">
          <h2 className="text-2xl font-bold text-slate-800 mb-6 text-center">
            {L.relatedEquipment}
          </h2>
          <div className="grid grid-cols-2 sm:grid-cols-4 gap-3">
            {Object.entries(equipmentData).filter(([slug]) => slug !== equipmentSlug).slice(0, 4).map(([slug, eq]) => (
              <Link key={slug} to={equipmentUrl(slug, lang)} className="bg-white rounded-xl p-4 shadow-sm border border-slate-200 hover:border-blue-300 hover:shadow-md transition text-center">
                <p className="text-sm font-semibold text-slate-800">{lang === 'pt' ? eq.name : eq.nameEn}</p>
                <p className="text-xs text-slate-500 mt-1">{L.fromPrice(eq.priceFrom)}</p>
              </Link>
            ))}
          </div>
        </div>
      </section>

      {/* CTA */}
      <section className="bg-slate-900 py-10 px-4">
        <div className="max-w-2xl mx-auto text-center">
          <h2 className="text-2xl font-bold text-white mb-3">
            {categoryUi[lang]?.readyToRent || 'Pronto para alugar?'}
          </h2>
          <p className="text-slate-400 mb-6 text-sm">
            {categoryUi[lang]?.readyBody || 'Reserve online em minutos. Entrega 24H em todo Portugal.'}
          </p>
          <div className="flex flex-col sm:flex-row gap-3 justify-center">
            <Link to={startPath} className={`${ctaPrimary} text-white font-bold px-6 py-3 rounded-xl transition`}>
              {categoryUi[lang]?.checkAvailability || '🔍 Ver Disponibilidade'}
            </Link>
            <a href="https://wa.me/351216035281" target="_blank" rel="noopener noreferrer" className={`${ctaWhats} text-white font-bold px-6 py-3 rounded-xl transition flex items-center justify-center gap-2`}>
              <MessageCircle className="w-5 h-5" /> WhatsApp
            </a>
          </div>
        </div>
      </section>

      {/* Footer */}
      <OrthocareFooter language={lang} />
    </div>
  );
}